import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { notify } from "react-notify-toast";
import PaginatedTable from "../../../shared/PaginatedTable";
import ConfirmationModal from "../../../shared/ConfirmationModal";
import CheckInModal from "./CheckInModal";
import AttendeesActions from "./AttendeesActions";
import AttendeesTableFilters from "./AttendeesTableFilters";
import AttendeesTableHeader from "./AttendeesTableHeader";
import AttendeesTableRecord from "./AttendeesTableRecord";

import TripEventsApi from "../../../../services/resources/TripEventsApi";
import { ApiCallErrorMessageHandler } from "../../../../lib/coc-common-scripts";
import { formatFullName, pluralizeText } from "../../../../lib";
import AuthService, {
  PermissionClaims,
} from "../../../../services/AuthService";
import axios from "axios";

// Parse a scanned QR identifier of the form "<AttendeeType>-<id>", returning
// null when the type is unrecognized or the id is missing.
const parseScannedIdentifier = (identifier) => {
  const [attendeeType, attendeeId] = identifier.split("-");

  const isStudent = attendeeType === "Student";
  const isEnrollmentAttendee =
    attendeeType === "Shliach" || attendeeType === "Chaperone";
  const isOtherAttendee = attendeeType === "OtherAttendee";

  if (
    (!isStudent && !isEnrollmentAttendee && !isOtherAttendee) ||
    !attendeeId
  ) {
    return null;
  }

  return { isStudent, isEnrollmentAttendee, isOtherAttendee, attendeeId };
};

function Attendees(props) {
  const {
    mobileMode,
    tripEvent,
    tripEvent: { id, isCheckInEnabled, isTravelTrip },
  } = props;

  const [success, setSuccess] = useState(true);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [results, setResults] = useState(12);
  const [showAttendeeCheckinModal, setShowAttendeeCheckinModal] =
    useState(false);
  const [revertAttendeeCheckinRecord, setRevertAttendeeCheckinRecord] =
    useState(null);
  const [attendees, setAttendees] = useState([]);
  const [totalAttendees, setTotalAttendees] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState({
    all: false,
    allExcept: [],
    selection: [],
  });
  const [scannedAttendeeIdentifier, setScannedAttendeeIdentifier] =
    useState("");
  const [enrolledShluchim, setEnrolledShluchim] = useState({
    shluchim: [],
    loading: false,
  });
  const [filters, setFilters] = useState({
    campusId: "",
    keyword: "",
    shliachId: "",
    statuses: ["Registered"],
    tourScheduleIds: [],
    trackIds: [],
    venueIds: [],
  });

  const apiSignalRef = useRef(axios.CancelToken.source());

  const getEnrolledShluchim = useCallback(() => {
    setEnrolledShluchim({
      shluchim: [],
      loading: true,
    });
    TripEventsApi.getAllTripEnrollments(apiSignalRef.current.token, id)
      .then((shluchim) => {
        setEnrolledShluchim({
          shluchim,
          loading: false,
        });
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setEnrolledShluchim({
            shluchim: [],
            loading: false,
          });
        }
      });
  }, [apiSignalRef, id]);

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  useEffect(() => {
    getEnrolledShluchim();
  }, [getEnrolledShluchim]);

  const getAttendees = useCallback(
    (_page = page, _results = results, _filters = filters) => {
      setPage(_page);
      setResults(_results);
      setLoading(true);
      setSuccess(true);

      TripEventsApi.getTripAttendees(
        apiSignalRef.current.token,
        id,
        _page,
        _results,
        undefined,
        _filters,
      )
        .then(({ results, numberOfRows }) => {
          setLoading(false);
          setAttendees(results);
          setTotalAttendees(numberOfRows);
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            setLoading(false);
            setAttendees([]);
            setSuccess(false);
            setTotalAttendees(0);
          }
        });
    },
    [page, results, filters, apiSignalRef, id],
  );

  const getAttendeesForExport = useCallback(async () => {
    const attendees = await TripEventsApi.getTripAttendees(
      apiSignalRef.current.token,
      id,
      page,
      results,
      undefined,
      filters,
      true,
    );

    return attendees.results;
  }, [apiSignalRef, id, page, results, filters]);

  const filterAttendees = useCallback(
    (_filters) => {
      setFilters(_filters);
      setSelectedAttendees({
        all: false,
        allExcept: [],
        selection: [],
      });
      getAttendees(1, results, _filters);
    },
    [getAttendees, results],
  );

  const attendeeApiParams = useCallback(() => {
    if (scannedAttendeeIdentifier) {
      const { isStudent, isEnrollmentAttendee, isOtherAttendee, attendeeId } =
        parseScannedIdentifier(scannedAttendeeIdentifier);

      return [
        apiSignalRef.current.token,
        id,
        isStudent ? [attendeeId] : [],
        isEnrollmentAttendee ? [attendeeId] : [],
        isOtherAttendee ? [attendeeId] : [],
      ];
    }

    const { all, allExcept, selection } = selectedAttendees;

    const byType = (attendees) => {
      const students = attendees.filter((a) => a.attendeeType === "Student");
      const otherAttendees = attendees.filter(
        (a) => a.attendeeType === "OtherAttendee",
      );
      const enrollmentAttendees = attendees.filter(
        (a) => a.attendeeType === "Shliach" || a.attendeeType === "Chaperone",
      );
      return { students, enrollmentAttendees, otherAttendees };
    };

    const selected = byType(selection);
    const deselected = byType(allExcept);

    return [
      apiSignalRef.current.token,
      id,
      !all ? selected.students.map((reg) => reg.id) : undefined,
      !all ? selected.enrollmentAttendees.map((reg) => reg.id) : undefined,
      !all ? selected.otherAttendees.map((reg) => reg.id) : undefined,
      all ? deselected.students.map((reg) => reg.id) : undefined,
      all ? deselected.enrollmentAttendees.map((reg) => reg.id) : undefined,
      all ? deselected.otherAttendees.map((reg) => reg.id) : undefined,
      all ? filters : undefined,
    ];
  }, [scannedAttendeeIdentifier, selectedAttendees, id, filters]);

  const getAttendeesDetailsForCheckin = useCallback(async () => {
    return await TripEventsApi.getTripAttendeeDetails(...attendeeApiParams());
  }, [attendeeApiParams]);

  const toggleSelectAttendee = useCallback(
    (attendee) => {
      const { all, allExcept, selection } = selectedAttendees;

      if (all) {
        const _allExcept =
          allExcept.map((s) => s.id).indexOf(attendee.id) >= 0
            ? allExcept.filter((s) => s.id !== attendee.id)
            : [...allExcept, attendee];
        setSelectedAttendees({
          ...selectedAttendees,
          allExcept: _allExcept,
        });
      } else {
        const _selection =
          selection.map((s) => s.id).indexOf(attendee.id) >= 0
            ? selection.filter((s) => s.id !== attendee.id)
            : [...selection, attendee];
        setSelectedAttendees({
          ...selectedAttendees,
          selection: _selection,
        });
      }
    },
    [selectedAttendees],
  );

  const toggleSelectAllAttendees = useCallback(() => {
    const { all, allExcept } = selectedAttendees;
    setSelectedAttendees({
      all: all && !allExcept.length ? false : true,
      allExcept: [],
      selection: [],
    });
  }, [selectedAttendees]);

  const showResultMessage = useCallback(
    ({
      numberOfEnrollmentAttendeesCheckedIn,
      numberOfEventAttendeesCheckedIn,
      numberOfStudentsCheckedIn,
    }) => {
      const enrollAttendeesResult = `${numberOfEnrollmentAttendeesCheckedIn} ${pluralizeText(
        "chaperone",
        numberOfEnrollmentAttendeesCheckedIn,
      )}/${pluralizeText(
        "shliach",
        numberOfEnrollmentAttendeesCheckedIn,
        "shluchim",
      )}`;
      const otherAttendeesResult = `${numberOfEventAttendeesCheckedIn} ${pluralizeText(
        "other attendee",
        numberOfEventAttendeesCheckedIn,
      )}`;
      const studentResults = `${numberOfStudentsCheckedIn} ${pluralizeText(
        "student",
        numberOfStudentsCheckedIn,
      )}`;

      const checkedInResults = [
        numberOfEnrollmentAttendeesCheckedIn && enrollAttendeesResult,
        numberOfEventAttendeesCheckedIn && otherAttendeesResult,
        numberOfStudentsCheckedIn && studentResults,
      ].filter(Boolean);

      // join as "a", "a and b", or "a, b and c"
      const results = checkedInResults.length
        ? `${checkedInResults
            .join(", ")
            .replace(/, ([^,]*)$/, " and $1")} checked in.`
        : "";

      notify.show(results, checkedInResults.length ? "success" : "error");
    },
    [],
  );

  const checkInAttendees = useCallback(() => {
    setErrorMessage("");
    setSubmissionLoading(true);

    TripEventsApi.checkinTripAttendees(...attendeeApiParams())
      .then((response) => {
        setShowAttendeeCheckinModal(false);
        setScannedAttendeeIdentifier("");
        setSubmissionLoading(false);
        setSelectedAttendees({
          all: false,
          allExcept: [],
          selection: [],
        });

        getAttendees(1);
        showResultMessage(response);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setErrorMessage(ApiCallErrorMessageHandler(err));
          setSubmissionLoading(false);
        }
      });
  }, [attendeeApiParams, getAttendees, showResultMessage]);

  const revertAttendeeCheckin = useCallback(async () => {
    setErrorMessage("");
    setSubmissionLoading(true);

    try {
      await TripEventsApi.revertTripAttendeeCheckin(
        apiSignalRef.current.token,
        id,
        {
          attendeeType: revertAttendeeCheckinRecord.attendeeType,
          attendeeRegistrationId: revertAttendeeCheckinRecord.id,
        },
      );
      getAttendees();
      setRevertAttendeeCheckinRecord(null);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setErrorMessage(ApiCallErrorMessageHandler(err));
      }
    }

    setSubmissionLoading(false);
  }, [getAttendees, id, revertAttendeeCheckinRecord]);

  const readOnlyAccess = !AuthService.UserHasClaim(
    PermissionClaims.TripDataEdit,
  );
  const allTourSchedules = useMemo(() => {
    return tripEvent.tours
      .map((tour) =>
        tour.schedules.map((s) => {
          return { id: s.id, name: `${tour.name} - ${s.name}` };
        }),
      )
      .flat();
  }, [tripEvent.tours]);

  return (
    <>
      <div className="flex flex-justify-space mb-24">
        <p className="xxl-text fw-500">All Attendees</p>
        <AttendeesActions
          tours={tripEvent.tours}
          getAttendeesForExport={getAttendeesForExport}
          hideCheckin={readOnlyAccess || !isCheckInEnabled}
          onCheckInAttendees={() => setShowAttendeeCheckinModal(true)}
          onScanAttendee={(identifier) => {
            if (!parseScannedIdentifier(identifier)) {
              notify.show(
                "Invalid QR code. Please scan a valid attendee QR code.",
                "error",
              );
              return;
            }
            setScannedAttendeeIdentifier(identifier);
            setShowAttendeeCheckinModal(true);
          }}
          tripEvent={tripEvent}
          selectedAttendees={selectedAttendees}
        />
      </div>

      <AttendeesTableFilters
        allEnrolledShluchim={enrolledShluchim.shluchim}
        allEnrolledShluchimLoading={enrolledShluchim.loading}
        allTourSchedules={allTourSchedules}
        allTracks={tripEvent.tracks}
        allVenues={tripEvent.tripVenues}
        applyFilters={filterAttendees}
        isCheckInEnabled={isCheckInEnabled}
        isTravelTrip={isTravelTrip}
      />

      <div className="trip-students-table">
        <PaginatedTable
          loading={loading}
          loadData={getAttendees}
          mobileMode={mobileMode}
          page={page}
          records={attendees}
          showResultsView={true}
          renderHeader={() => (
            <AttendeesTableHeader
              canSelectAttendees={attendees.find(
                (a) => a.status === "Registered",
              )}
              isCheckInEnabled={isCheckInEnabled}
              isTravelTrip={isTravelTrip}
              readOnlyAccess={readOnlyAccess}
              selectedAttendees={selectedAttendees}
              toggleSelectAllAttendees={toggleSelectAllAttendees}
            />
          )}
          renderRow={(attendee) => (
            <AttendeesTableRecord
              attendee={attendee}
              key={`${attendee.id}-${attendee.attendeeType}`}
              onRevertAttendeeCheckin={() =>
                setRevertAttendeeCheckinRecord(attendee)
              }
              isCheckInEnabled={isCheckInEnabled}
              isTravelTrip={isTravelTrip}
              readOnlyAccess={readOnlyAccess}
              scheduleId={tripEvent.programScheduleID}
              selected={
                selectedAttendees.all
                  ? !selectedAttendees.allExcept.find(
                      (a) =>
                        a.id === attendee.id &&
                        a.attendeeType === attendee.attendeeType,
                    )
                  : selectedAttendees.selection.find(
                      (a) =>
                        a.id === attendee.id &&
                        a.attendeeType === attendee.attendeeType,
                    )
              }
              toggleSelectAttendee={() => toggleSelectAttendee(attendee)}
            />
          )}
          results={results}
          success={success}
          totalCount={totalAttendees}
          preHeaderMargin={12}
        />
      </div>
      {!loading && success && attendees.length > 0 && (
        <p className="accent-text-dark mt-24">
          {totalAttendees} Result{totalAttendees === 1 ? "" : "s"}
        </p>
      )}

      {showAttendeeCheckinModal && (
        <CheckInModal
          onCancel={() => {
            setShowAttendeeCheckinModal(false);
            setScannedAttendeeIdentifier("");
            setErrorMessage("");
          }}
          onConfirm={checkInAttendees}
          errorMessage={errorMessage}
          getAttendeesDetailsForCheckin={getAttendeesDetailsForCheckin}
          submissionLoading={submissionLoading}
          selectedAttendees={selectedAttendees}
          scannedAttendeeIdentifier={scannedAttendeeIdentifier}
          tours={tripEvent.tours}
          tracks={tripEvent.tracks}
          show={showAttendeeCheckinModal}
        />
      )}

      {revertAttendeeCheckinRecord && (
        <ConfirmationModal
          cancel={() => {
            setRevertAttendeeCheckinRecord(null);
            setErrorMessage("");
          }}
          cancelText="No"
          confirm={revertAttendeeCheckin}
          confirmText="Yes, undo check in"
          errorMessage={errorMessage}
          loading={submissionLoading}
          message={`Are you sure who want to undo the check in for ${formatFullName(
            revertAttendeeCheckinRecord.firstName,
            revertAttendeeCheckinRecord.lastName,
          )}? This will revert their status back to Registered.`}
          show={true}
          title="Undo checkin"
        />
      )}
    </>
  );
}
export default React.memo(Attendees);
